import { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  CircleDashed,
  Copy,
  ExternalLink,
  FileCheck2,
  Loader2,
  LockKeyhole,
  RefreshCw,
  Rocket,
  ShieldCheck,
  X,
  XCircle,
} from "lucide-react";
import { useStudioLocale } from "@/i18n";
import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";
import {
  getStudioPublishDeployCopy,
  type StudioPublishDeployCopy,
} from "@/i18n/studio-publish-deploy-copy";
import { flushSaves } from "@/store/auto-save";
import { flushEditorFullPersist } from "@/store/auto-save-bridge";
import { publicationEnvironmentsForDistribution } from "@/services/appliance-publication-target";
import {
  presentPublicationReleaseError,
  type PublicationReleaseErrorPresentation,
} from "./publication-release-error";
import type { PublicationReleaseController } from "./use-publication-release";
import {
  PUBLICATION_VERIFICATION_AXES,
  publicationVerifiedDeploymentUrl,
  type PublicationReleaseAction,
  type PublicationReleaseProjection,
  type PublicationVerificationAxisProjection,
} from "./publication-release-types";
import { publicationStateLabel } from "./publication-release-labels";

type ReleaseCopy = StudioPublishDeployCopy["release"];

type PublicationPanelMode = "candidate" | "launch";
type ReleaseEnvironment = "staging" | "production";

interface PublicationReleasePanelProps {
  mode: PublicationPanelMode;
  controller: PublicationReleaseController;
  projectId: string | null | undefined;
  slug?: string | null;
  readOnly?: boolean;
}

interface ConfirmationState {
  kind: "deploy" | "rollback" | "take-down";
  title: string;
  description: string;
  environment: ReleaseEnvironment;
  targetReceiptHash?: string;
}

export function PublicationReleasePanel({
  mode,
  controller,
  projectId,
  slug,
  readOnly = false,
}: PublicationReleasePanelProps) {
  const { locale } = useStudioLocale();
  const copy = getStudioPublishDeployCopy(locale).release;
  // What this build can actually deploy to. Two on a cloud build (a real
  // choice between two hosted sites); ONE on an appliance, whose own control
  // plane admits only `authorityScope: "appliance-local"` and refuses
  // `productionEligible !== false`. Element 0 is `staging` in both lists, so
  // the environment sent to the server is unchanged either way.
  const environments = publicationEnvironmentsForDistribution();
  const [environment, setEnvironment] = useState<ReleaseEnvironment>(
    environments[0] ?? "staging",
  );
  const [confirmation, setConfirmation] = useState<ConfirmationState | null>(
    null,
  );
  const [actionError, setActionError] =
    useState<PublicationReleaseErrorPresentation | null>(null);
  const release = controller.release;
  const isBusy = controller.pendingAction !== null;
  const verifiedDeploymentUrl = publicationVerifiedDeploymentUrl(release);
  const currentReceiptHash = release?.deployment?.receiptRef?.contentHash;
  const previousReceipt = release?.rollbackTargets.find(
    (target) => target.environment === release.deployment?.environment,
  )?.receiptRef.contentHash;
  /**
   * §F1 — the exact conditions the rollback and take-down buttons render
   * under, read ONCE so the sentence above them and the buttons themselves
   * cannot disagree. Deriving this from the same expressions (rather than
   * restating them) is the point: the measured defect was two places
   * describing one state and reaching opposite conclusions.
   */
  const recoveryActionsOffered = Boolean(
    mode === "launch" &&
      release &&
      ((previousReceipt && release.allowedActions.includes("rollback")) ||
        (currentReceiptHash && release.allowedActions.includes("take-down"))),
  );
  const controllerError = useMemo(
    () =>
      controller.error
        ? presentPublicationReleaseError(controller.error, locale)
        : null,
    [controller.error, locale],
  );
  const visibleErrors = useMemo(() => {
    const values = [actionError, controllerError].filter(
      (value): value is PublicationReleaseErrorPresentation => Boolean(value),
    );
    return values.filter(
      (value, index) =>
        values.findIndex(
          (candidate) =>
            candidate.kind === value.kind &&
            candidate.message === value.message,
        ) === index,
    );
  }, [actionError, controllerError]);

  useEffect(() => {
    if (!confirmation || isBusy) return;
    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setConfirmation(null);
    };
    document.addEventListener("keydown", dismissOnEscape, true);
    return () => document.removeEventListener("keydown", dismissOnEscape, true);
  }, [confirmation, isBusy]);

  const primary = useMemo(
    () =>
      selectPrimaryAction(
        copy,
        mode,
        environment,
        release,
        controller.executionCredentialEnvironments.includes(environment),
        Boolean(
          release &&
          controller.selectedTargetProfileId &&
          (controller.selectedTargetProfileId !==
            release.targetProfile.profileId ||
            controller.selectedTargetProfileVersion !==
              release.targetProfile.profileVersion),
        ),
      ),
    [
      controller.executionCredentialEnvironments,
      controller.selectedTargetProfileId,
      controller.selectedTargetProfileVersion,
      copy,
      environment,
      mode,
      release,
    ],
  );

  async function executePrimaryAction() {
    if (!primary || isBusy) return;
    // The blocker was already told to the user at the offer (see
    // `selectPrimaryAction`), so there is nothing left to discover behind a
    // confirmation. §5.3g.
    if (primary.blockedReason) return;
    setActionError(null);
    try {
      if (primary.action === "prepare-candidate") {
        await flushEditorFullPersist();
        await flushSaves();
        await controller.prepareCandidate();
        return;
      }
      if (primary.action === "run-verification") {
        await controller.runVerification();
        return;
      }
      if (primary.action === "request-staging-grant") {
        await controller.requestStagingGrant();
        return;
      }
      if (primary.action === "request-authorization") {
        await controller.requestAuthorization();
        return;
      }
      if (primary.action === "refresh-readback") {
        await controller.refreshReadback();
        return;
      }
      if (
        primary.action === "deploy-staging" ||
        primary.action === "deploy-production"
      ) {
        if (!release?.outputManifestRef)
          throw new Error(copy.guards.deployOutputManifestMissing);
        setConfirmation({
          kind: "deploy",
          title:
            primary.action === "deploy-production"
              ? copy.confirm.deployProductionTitle
              : copy.confirm.deployStagingTitle,
          description: copy.confirm.deployDetail(
            release.targetProfile.profileId,
            shortHash(release.outputManifestRef.contentHash) ?? copy.notAvailable,
            environment === "production" ? copy.production : copy.staging,
          ),
          environment,
        });
      }
    } catch (cause) {
      setActionError(presentPublicationReleaseError(cause, locale));
    }
  }

  async function refreshRelease() {
    setActionError(null);
    try {
      await controller.refresh();
    } catch (cause) {
      setActionError(presentPublicationReleaseError(cause, locale));
    }
  }

  async function confirmExternalEffect() {
    if (!confirmation) return;
    // §5.3f: each kind checks ITS OWN precondition, and every refusal is
    // thrown-and-shown. The old shared `!release?.outputManifestRef` early
    // return gated rollback and take-down on a *deploy* precondition — and
    // silently: measured 2026-08-18 (slice 6, test B), the confirm sent no
    // request, said nothing, and left the dialog open.
    try {
      if (confirmation.kind === "deploy") {
        if (!release?.outputManifestRef)
          throw new Error(copy.guards.deployOutputManifestMissing);
        const adapterId = release.targetProfile.deploymentProfile;
        if (!adapterId)
          throw new Error(copy.guards.noAdapter);
        // The production-promotion check used to THROW here, i.e. after the
        // user had read a confirmation describing the deploy and pressed the
        // final button. It now decides whether the action is offered at all —
        // see `selectPrimaryAction`. §5.3g.
        const promotionHash = release.promotionRef?.contentHash;
        await controller.deploy({
          environment: confirmation.environment,
          adapterId,
          confirmation: {
            confirmed: true,
            candidateId: release.candidateId,
            outputHash: release.outputHash,
            ...(promotionHash ? { promotionHash } : {}),
          },
        });
      } else if (confirmation.kind === "rollback") {
        if (!confirmation.targetReceiptHash)
          throw new Error(copy.guards.effectTargetReceiptMissing);
        await controller.rollback(confirmation.targetReceiptHash);
      } else if (confirmation.kind === "take-down") {
        if (!confirmation.targetReceiptHash)
          throw new Error(copy.guards.effectTargetReceiptMissing);
        await controller.takeDown(confirmation.targetReceiptHash);
      }
      setConfirmation(null);
    } catch (cause) {
      // A confirmed request can fail after the provider has accepted the
      // idempotent effect. Close the modal first so release reconciliation is
      // immediately operable from the page-level recovery banner.
      setConfirmation(null);
      setActionError(presentPublicationReleaseError(cause, locale));
    }
  }

  if (controller.status === "loading" && !release) {
    return (
      <section
        className="rounded-lg border border-border bg-card p-4"
        aria-live="polite"
      >
        <div className="flex min-h-24 items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2
            className="h-4 w-4 animate-spin motion-reduce:animate-none"
            aria-hidden
          />
          {copy.loading}
        </div>
      </section>
    );
  }

  return (
    <section
      className="rounded-lg border border-border bg-card"
      data-testid={`publication-release-panel-${mode}`}
      data-publication-slug={slug ?? undefined}
      aria-labelledby={`publication-release-${mode}-title`}
    >
      <div className="flex flex-col gap-4 border-b border-border p-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2
              id={`publication-release-${mode}-title`}
              className="text-sm font-semibold text-foreground"
            >
              {mode === "candidate" ? copy.candidateTitle : copy.launchTitle}
            </h2>
            <ReleaseStateBadge release={release} locale={locale} />
          </div>
          <p className="mt-1 max-w-2xl text-xs leading-5 text-muted-foreground">
            {mode === "candidate" ? copy.candidateDetail : copy.launchDetail}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void refreshRelease()}
          disabled={!projectId || controller.status === "loading" || isBusy}
          className="inline-flex h-11 shrink-0 cursor-pointer items-center justify-center gap-2 rounded-md border border-border bg-background px-3 text-xs font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
        >
          <RefreshCw
            className={`h-3.5 w-3.5 ${controller.status === "loading" ? "animate-spin motion-reduce:animate-none" : ""}`}
            aria-hidden
          />
          {copy.refresh}
        </button>
      </div>

      <PublicationAuthorityBoundary copy={copy} release={release} />

      {visibleErrors.length > 0 && (
        <PublicationErrorBanner
          copy={copy}
          errors={visibleErrors}
          busy={isBusy || controller.status === "loading"}
          onRefresh={() => void refreshRelease()}
        />
      )}

      {!projectId ? (
        <EmptyReleaseState
          title={copy.empty.openProjectTitle}
          detail={copy.empty.openProjectDetail}
        />
      ) : !release ? (
        <div className="p-4">
          <EmptyReleaseState
            title={copy.empty.noCandidateTitle}
            detail={copy.empty.noCandidateDetail}
          />
          {mode === "candidate" && !readOnly && (
            <TargetProfileSelect copy={copy} controller={controller} />
          )}
          {mode === "candidate" && !readOnly && (
            <PrimaryActionButton
              label={copy.actions.prepareCandidate}
              busyLabel={copy.actions.prepareCandidateBusy}
              busy={controller.pendingAction === "prepare-candidate"}
              disabled={
                isBusy ||
                !controller.selectedTargetProfileId ||
                !controller.selectedTargetProfileVersion
              }
              icon={FileCheck2}
              onClick={() => void executePrimaryAction()}
            />
          )}
        </div>
      ) : (
        <>
          <div className="grid gap-px bg-border lg:grid-cols-2">
            <ReleaseIdentityCard copy={copy} release={release} />
            <ReleaseAuthorityCard copy={copy} release={release} />
          </div>

          <VerificationMatrix copy={copy} release={release} />

          <ReleaseProofAndReadbackDetails copy={copy} release={release} />

          {release.blockers.length > 0 && (
            <div
              className="border-t border-border p-4"
              data-testid="publication-release-blockers"
            >
              <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
                <AlertTriangle
                  className="h-4 w-4 text-muted-foreground"
                  aria-hidden
                />
                {copy.blockersHeading}
              </div>
              <ul className="mt-2 space-y-1.5">
                {release.blockers.map((blocker) => (
                  <li
                    key={blocker}
                    className="flex gap-2 text-xs leading-5 text-muted-foreground"
                  >
                    <span
                      className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground"
                      aria-hidden
                    />
                    <span>{blocker}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!readOnly && (
            <div className="border-t border-border p-4">
              {mode === "candidate" && (
                <TargetProfileSelect copy={copy} controller={controller} />
              )}
              {mode === "launch" && (
                <fieldset className="mb-4">
                  <legend className="text-xs font-medium text-foreground">
                    {copy.environmentLegend}
                  </legend>
                  {environments.length < 2 ? (
                    <div
                      className="mt-2 rounded-md border border-border bg-muted/40 px-3 py-2"
                      data-testid="publication-deployment-target"
                    >
                      <p className="text-xs font-medium text-foreground">
                        {copy.applianceTarget.statement}
                      </p>
                      <p className="mt-1 text-xs leading-5 text-muted-foreground">
                        {copy.applianceTarget.detail}
                      </p>
                    </div>
                  ) : (
                    <div
                      className="mt-2 grid grid-cols-2 gap-2"
                      role="radiogroup"
                    >
                      {environments.map((value) => (
                        <label
                          key={value}
                          className={`flex min-h-11 cursor-pointer items-center gap-2 rounded-md border px-3 text-xs transition-colors focus-within:ring-2 focus-within:ring-ring ${
                            environment === value
                              ? "border-foreground bg-muted text-foreground"
                              : "border-border bg-background text-muted-foreground"
                          }`}
                        >
                          <input
                            type="radio"
                            name="publication-environment"
                            value={value}
                            checked={environment === value}
                            onChange={() => setEnvironment(value)}
                            className="h-4 w-4"
                          />
                          {value === "staging" ? copy.staging : copy.production}
                        </label>
                      ))}
                    </div>
                  )}
                </fieldset>
              )}

              {primary ? (
                <div className="space-y-1.5">
                  <PrimaryActionButton
                    label={primary.label}
                    busyLabel={primary.busyLabel}
                    busy={controller.pendingAction === primary.action}
                    disabled={
                      isBusy ||
                      Boolean(primary.blockedReason) ||
                      !release.allowedActions.includes(primary.action)
                    }
                    describedBy={
                      primary.blockedReason
                        ? "publication-primary-action-blocker"
                        : undefined
                    }
                    title={primary.blockedReason}
                    icon={primary.icon}
                    onClick={() => void executePrimaryAction()}
                  />
                  {primary.blockedReason && (
                    <p
                      id="publication-primary-action-blocker"
                      data-testid="publication-primary-action-blocker"
                      className="text-xs leading-5 text-muted-foreground"
                    >
                      {primary.blockedReason}
                    </p>
                  )}
                </div>
              ) : (
                <div className="rounded-md border border-border bg-muted/40 px-3 py-3 text-xs text-muted-foreground">
                  {mode === "candidate"
                    ? copy.candidateReadyNote
                    : // §F1 — `selectPrimaryAction` returns null once a
                      // same-environment deployment carries a verified URL,
                      // and this branch used to print "No launch action is
                      // currently allowed" one line ABOVE the rollback and
                      // take-down buttons. Rollback and take-down are not
                      // "launch actions" in that function's vocabulary, but
                      // they are actions, and they are irreversible. The
                      // sentence now depends on the same two conditions the
                      // buttons below do, so the two cannot disagree.
                      recoveryActionsOffered
                      ? copy.noForwardActionWithRecoveryNote
                      : copy.noLaunchActionNote}
                </div>
              )}

              {mode === "launch" && verifiedDeploymentUrl && (
                <a
                  href={verifiedDeploymentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-border bg-background px-3 text-xs font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <ExternalLink className="h-4 w-4" aria-hidden />
                  {copy.openVerifiedDeployment}
                </a>
              )}

              {mode === "launch" &&
                previousReceipt &&
                release.allowedActions.includes("rollback") && (
                  <button
                    type="button"
                    onClick={() =>
                      setConfirmation({
                        kind: "rollback",
                        title: copy.confirm.rollbackTitle,
                        description: copy.confirm.rollbackDetail(
                          shortHash(previousReceipt) ?? copy.notAvailable,
                        ),
                        environment:
                          release.deployment?.environment === "production"
                            ? "production"
                            : "staging",
                        targetReceiptHash: previousReceipt,
                      })
                    }
                    disabled={isBusy}
                    className="mt-2 inline-flex h-11 w-full cursor-pointer items-center justify-center rounded-md border border-destructive/30 bg-background px-3 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {copy.rollbackButton}
                  </button>
                )}

              {mode === "launch" &&
                currentReceiptHash &&
                release.allowedActions.includes("take-down") && (
                  <button
                    type="button"
                    onClick={() =>
                      setConfirmation({
                        kind: "take-down",
                        // §F8 — this used to receive
                        // `release.deployment?.environment`, the transport
                        // value, so the ko appliance read
                        // `이 staging 배포를 내릴까요?`: a Korean sentence with
                        // an English enum in the middle, at a destructive
                        // confirmation.
                        title: copy.confirm.takeDownTitle(
                          environmentLabel(
                            copy,
                            release.deployment?.environment,
                          ),
                        ),
                        description: copy.confirm.takeDownDetail(
                          shortHash(currentReceiptHash) ?? copy.notAvailable,
                        ),
                        environment:
                          release.deployment?.environment === "production"
                            ? "production"
                            : "staging",
                        targetReceiptHash: currentReceiptHash,
                      })
                    }
                    disabled={isBusy}
                    className="mt-2 inline-flex h-11 w-full cursor-pointer items-center justify-center rounded-md border border-destructive bg-destructive px-3 text-xs font-semibold text-destructive-foreground transition-colors hover:bg-destructive/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {copy.takeDownButton}
                  </button>
                )}
            </div>
          )}

          <ReleaseLedger
            copy={copy}
            locale={locale}
            releases={controller.releases}
            current={release}
          />
        </>
      )}

      <ExternalEffectConfirmation
        copy={copy}
        confirmation={confirmation}
        busy={isBusy}
        onOpenChange={(open) => {
          if (!open && !isBusy) setConfirmation(null);
        }}
        onConfirm={() => void confirmExternalEffect()}
      />
    </section>
  );
}

function PublicationAuthorityBoundary({
  copy,
  release,
}: {
  copy: ReleaseCopy;
  release: PublicationReleaseProjection | null;
}) {
  const productionReady = release?.derivedState === "production_ready";
  const rolledBack = release?.derivedState === "rolled_back";
  const takenDown = release?.derivedState === "taken_down";
  const profileLabel = release
    ? `${release.targetProfile.lifecycle} / ${release.targetProfile.supportStatus}`
    : copy.absent.noCandidate;

  return (
    <div
      className="border-b border-border bg-muted/40 px-4 py-3"
      data-testid="publication-authority-boundary"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 gap-2">
          {productionReady ? (
            <CheckCircle2
              className="mt-0.5 h-4 w-4 shrink-0 text-foreground"
              aria-hidden
            />
          ) : (
            <LockKeyhole
              className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
              aria-hidden
            />
          )}
          <div>
            <p className="text-xs font-semibold text-foreground">
              {productionReady
                ? copy.authority.liveCheckPassed
                : rolledBack
                  ? copy.authority.rolledBack
                  : takenDown
                    ? copy.authority.takenDown
                    : copy.authority.failClosed}
            </p>
            <p className="mt-1 max-w-2xl text-[11px] leading-4 text-muted-foreground">
              {productionReady
                ? copy.authority.passedDetail
                : copy.authority.failClosedDetail}
            </p>
          </div>
        </div>
        <span className="inline-flex shrink-0 items-center rounded-full border border-border bg-background px-2 py-1 font-mono text-[10px] text-foreground">
          {productionReady
            ? copy.authority.productionReadyBadge
            : profileLabel}
        </span>
      </div>
    </div>
  );
}

function PublicationErrorBanner({
  copy,
  errors,
  busy,
  onRefresh,
}: {
  copy: ReleaseCopy;
  errors: PublicationReleaseErrorPresentation[];
  busy: boolean;
  onRefresh: () => void;
}) {
  const refreshFirst = errors.some((error) => error.refreshFirst);
  return (
    <div
      role="alert"
      aria-live="assertive"
      className="border-b border-destructive/20 bg-destructive/10 px-4 py-3 text-xs text-destructive"
      data-testid="publication-release-error"
    >
      <div className="space-y-3">
        {errors.map((error) => (
          <div key={`${error.kind}:${error.message}`}>
            <p className="font-semibold">{error.title}</p>
            <p className="mt-1 break-words">{error.message}</p>
            <p className="mt-1 text-foreground">
              {copy.errorBanner.recoveryPrefix} {error.recovery}
            </p>
          </div>
        ))}
      </div>
      {refreshFirst && (
        <button
          type="button"
          onClick={onRefresh}
          disabled={busy}
          className="mt-3 inline-flex h-11 cursor-pointer items-center justify-center gap-2 rounded-md border border-destructive/30 bg-background px-3 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
        >
          <RefreshCw className="h-3.5 w-3.5" aria-hidden />
          {copy.errorBanner.refreshBeforeRetry}
        </button>
      )}
    </div>
  );
}

function ReleaseIdentityCard({
  copy,
  release,
}: {
  copy: ReleaseCopy;
  release: PublicationReleaseProjection;
}) {
  return (
    <article className="min-w-0 bg-card p-4">
      <h3 className="text-xs font-semibold text-foreground">
        {copy.identity.heading}
      </h3>
      <dl className="mt-3 grid gap-2 text-xs">
        <Fact copy={copy} label={copy.identity.release} value={release.releaseId} />
        <Fact
          copy={copy}
          label={copy.identity.revision}
          value={shortHash(release.sourceRevision.contentHash)}
          warning={release.sourceRevision.stale}
        />
        <Fact
          copy={copy}
          label={copy.identity.package}
          value={shortHash(release.applicationPackageRef?.contentHash)}
        />
        <Fact
          copy={copy}
          label={copy.identity.plan}
          value={shortHash(release.planRef?.contentHash)}
        />
        <Fact
          copy={copy}
          label={copy.identity.manifest}
          value={shortHash(release.outputManifestRef?.contentHash)}
        />
        <Fact
          copy={copy}
          label={copy.identity.promotion}
          value={shortHash(release.promotionRef?.contentHash)}
          warning={!release.promotionRef}
        />
      </dl>
      <div
        className="mt-4 border-t border-border pt-3"
        data-testid="publication-compiler-identity"
      >
        <h4 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {copy.identity.signingHeading}
        </h4>
        {release.compilerIdentity.availability === "available" ? (
          <>
            <div className="mt-2 flex items-start gap-2 rounded-md border border-border bg-background p-2.5">
              <LockKeyhole
                className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground"
                aria-hidden
              />
              <p className="text-[11px] leading-4 text-foreground">
                {copy.identity.unsignedNote}
              </p>
            </div>
            <dl className="mt-3 grid gap-2 text-xs">
              <Fact
                copy={copy}
                label={copy.identity.compiler}
                value={`${release.compilerIdentity.compilerId}@${release.compilerIdentity.compilerVersion}`}
              />
              <Fact
                copy={copy}
                label={copy.identity.implementation}
                value={release.compilerIdentity.implementationRef}
              />
              <Fact
                copy={copy}
                label={copy.identity.artifact}
                value={release.compilerIdentity.compilerArtifactRef.ref}
              />
              <CopyableCompilerHash
                copy={copy}
                value={release.compilerIdentity.compilerArtifactRef.contentHash}
              />
              <Fact
                copy={copy}
                label={copy.identity.scope}
                value={release.compilerIdentity.artifactAttestation.scope}
                warning
              />
              <Fact
                copy={copy}
                label={copy.identity.signature}
                value={
                  release.compilerIdentity.artifactAttestation.signatureStatus
                }
                warning
              />
            </dl>
          </>
        ) : (
          <div
            className="mt-2 rounded-md border border-border bg-background p-2.5 text-[11px] leading-4 text-muted-foreground"
            role="status"
          >
            {copy.identity.signingUnavailable}
          </div>
        )}
      </div>
    </article>
  );
}

function CopyableCompilerHash({
  copy,
  value,
}: {
  copy: ReleaseCopy;
  value: string;
}) {
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "failed">(
    "idle",
  );

  useEffect(() => setCopyStatus("idle"), [value]);

  async function copyHash() {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("clipboard-missing");
      await navigator.clipboard.writeText(value);
      setCopyStatus("copied");
    } catch {
      setCopyStatus("failed");
    }
  }

  return (
    <div className="grid gap-2 border-t border-border/60 pt-2">
      <dt className="text-muted-foreground">{copy.identity.artifactHash}</dt>
      <dd className="min-w-0">
        <code
          className="block whitespace-normal break-all font-mono text-[11px] leading-5 text-foreground"
          data-testid="publication-compiler-artifact-hash"
        >
          {value}
        </code>
        <button
          type="button"
          onClick={() => void copyHash()}
          aria-label={copy.identity.copyHashAria}
          className="mt-2 inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-md border border-border bg-background px-3 text-[11px] font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
        >
          {copyStatus === "copied" ? (
            <Check className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <Copy className="h-3.5 w-3.5" aria-hidden />
          )}
          <span aria-live="polite">
            {copyStatus === "copied"
              ? copy.identity.copied
              : copyStatus === "failed"
                ? copy.identity.copyFailed
                : copy.identity.copyFullHash}
          </span>
        </button>
      </dd>
    </div>
  );
}

function TargetProfileSelect({
  copy,
  controller,
}: {
  copy: ReleaseCopy;
  controller: PublicationReleaseController;
}) {
  const selected = controller.targetProfiles.find(
    (profile) =>
      profile.profileId === controller.selectedTargetProfileId &&
      profile.profileVersion === controller.selectedTargetProfileVersion,
  );
  const selectedKey = selected
    ? `${selected.profileId}\u0000${selected.profileVersion}`
    : "";
  return (
    <div className="mb-4 rounded-md border border-border bg-background p-3">
      <label
        htmlFor="publication-target-profile"
        className="text-xs font-medium text-foreground"
      >
        {copy.targetProfile.label}
      </label>
      <select
        id="publication-target-profile"
        value={selectedKey}
        onChange={(event) => {
          const [profileId, profileVersion] =
            event.target.value.split("\u0000");
          if (profileId && profileVersion)
            controller.selectTargetProfile(profileId, profileVersion);
        }}
        disabled={
          controller.targetProfiles.length === 0 ||
          controller.pendingAction !== null
        }
        className="mt-2 h-11 w-full rounded-md border border-input bg-background px-3 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
      >
        {controller.targetProfiles.length === 0 && (
          <option value="">{copy.targetProfile.none}</option>
        )}
        {controller.targetProfiles.map((profile) => (
          <option
            key={`${profile.profileId}:${profile.profileVersion}`}
            value={`${profile.profileId}\u0000${profile.profileVersion}`}
          >
            {profile.title ?? profile.profileId} · {profile.profileVersion} ·{" "}
            {profile.lifecycle}/{profile.supportStatus}
          </option>
        ))}
      </select>
      {selected && (
        <p className="mt-2 text-[11px] leading-4 text-muted-foreground">
          {copy.targetProfile.adapterPrefix}{" "}
          {selected.deploymentProfile ?? copy.absent.notRegistered}
          {selected.openGaps.length > 0
            ? ` · ${copy.targetProfile.openGaps(selected.openGaps.length)}`
            : ` · ${copy.targetProfile.noGaps}`}
        </p>
      )}
    </div>
  );
}

function ReleaseAuthorityCard({
  copy,
  release,
}: {
  copy: ReleaseCopy;
  release: PublicationReleaseProjection;
}) {
  return (
    <article className="min-w-0 bg-card p-4">
      <h3 className="text-xs font-semibold text-foreground">
        {copy.authorityCard.heading}
      </h3>
      <dl className="mt-3 grid gap-2 text-xs">
        <Fact
          copy={copy}
          label={copy.authorityCard.target}
          value={`${release.targetProfile.profileId}@${release.targetProfile.profileVersion}`}
        />
        <Fact
          copy={copy}
          label={copy.authorityCard.profile}
          value={`${release.targetProfile.lifecycle} / ${release.targetProfile.supportStatus}`}
          warning={
            release.targetProfile.lifecycle !== "active" ||
            release.targetProfile.supportStatus !== "supported"
          }
        />
        <Fact
          copy={copy}
          label={copy.authorityCard.adapter}
          value={
            release.targetProfile.deploymentProfile ?? copy.absent.notRegistered
          }
          warning={!release.targetProfile.deploymentProfile}
        />
        <Fact
          copy={copy}
          label={copy.authorityCard.authorization}
          value={release.authorization?.status ?? copy.absent.notIssued}
          warning={
            release.authorization?.status !== "verified" &&
            release.authorization?.status !== "consumed"
          }
        />
        <Fact
          copy={copy}
          label={copy.authorityCard.deployment}
          value={release.deployment?.status ?? copy.absent.notStarted}
          warning={release.deployment?.status !== "verified"}
        />
        <Fact
          copy={copy}
          label={copy.authorityCard.liveCheck}
          value={release.readback?.status ?? copy.absent.notStarted}
          warning={release.readback?.status !== "pass"}
        />
      </dl>
      {release.targetProfile.openGaps.length > 0 && (
        <div className="mt-3 rounded-md border border-border bg-background p-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {copy.authorityCard.openGapsHeading}
          </p>
          <ul className="mt-1.5 space-y-1">
            {release.targetProfile.openGaps.map((gap) => (
              <li key={gap} className="text-[11px] leading-4 text-foreground">
                {gap}
              </li>
            ))}
          </ul>
        </div>
      )}
    </article>
  );
}

function VerificationMatrix({
  copy,
  release,
}: {
  copy: ReleaseCopy;
  release: PublicationReleaseProjection;
}) {
  const matrix =
    release.postDeployVerificationMatrix ?? release.verificationMatrix;
  const proofArtifacts = new Map(
    (release.proofArtifactRefs ?? []).map((artifact) => [
      artifact.ref,
      artifact,
    ]),
  );
  const axes =
    matrix?.axes ??
    PUBLICATION_VERIFICATION_AXES.map((axis) => ({
      axis,
      status: "not-proven" as const,
      evidenceRefs: [],
      notes: [copy.matrix.axisNotProduced],
    }));
  return (
    <div
      className="border-t border-border p-4"
      data-testid="publication-verification-matrix"
    >
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-xs font-semibold text-foreground">
            {copy.matrix.heading}
          </h3>
          <p className="mt-1 text-[11px] leading-4 text-muted-foreground">
            {release.postDeployVerificationMatrix
              ? copy.matrix.postDeployNote
              : copy.matrix.candidateNote}
          </p>
        </div>
        <div className="mt-2 flex gap-2 sm:mt-0">
          <GateBadge
            copy={copy}
            label={copy.matrix.candidateGate}
            status={matrix?.candidateGate.status ?? "blocked"}
          />
          <GateBadge
            copy={copy}
            label={copy.matrix.productionGate}
            status={matrix?.productionGate.status ?? "blocked"}
          />
        </div>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {axes.map((axis) => (
          <AxisRow
            key={axis.axis}
            copy={copy}
            axis={axis}
            proofArtifacts={proofArtifacts}
          />
        ))}
      </div>
    </div>
  );
}

function ReleaseProofAndReadbackDetails({
  copy,
  release,
}: {
  copy: ReleaseCopy;
  release: PublicationReleaseProjection;
}) {
  const matrix =
    release.postDeployVerificationMatrix ?? release.verificationMatrix;
  const deployment = release.deployment;
  const readback = release.readback;
  const verifiedUrl = publicationVerifiedDeploymentUrl(release);
  const deploymentFailures = deployment?.failureReasons ?? [];
  const readbackFailures = readback?.failureReasons ?? [];

  return (
    <details
      className="border-t border-border bg-card"
      open={Boolean(deployment || readback)}
      data-testid="publication-proof-readback-details"
    >
      <summary className="flex min-h-12 cursor-pointer items-center justify-between gap-3 px-4 py-3 text-xs font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <span>{copy.proof.summary}</span>
        <span className="shrink-0 font-mono text-[10px] font-normal text-muted-foreground">
          {copy.proof.contentAddressed}
        </span>
      </summary>
      <div className="grid gap-px border-t border-border bg-border lg:grid-cols-3">
        <article className="min-w-0 bg-card p-4">
          <h4 className="text-xs font-semibold text-foreground">
            {copy.proof.candidateEvidenceHeading}
          </h4>
          <dl className="mt-3 grid gap-2 text-xs">
            <RefFact
              copy={copy}
              label={copy.proof.matrix}
              value={
                matrix
                  ? {
                      ref: release.postDeployVerificationMatrix
                        ? copy.proof.postDeployMatrixRef
                        : copy.proof.candidateMatrixRef,
                      contentHash: matrix.contentHash,
                    }
                  : undefined
              }
            />
            <RefFact
              copy={copy}
              label={copy.proof.signedRecord}
              value={release.attestationRef}
            />
            <RefFact
              copy={copy}
              label={copy.proof.promotion}
              value={release.promotionRef}
            />
            <Fact
              copy={copy}
              label={copy.proof.candidateGate}
              value={copy.gateStatus[matrix?.candidateGate.status ?? "blocked"]}
              warning={matrix?.candidateGate.status !== "ready"}
            />
            <Fact
              copy={copy}
              label={copy.proof.productionGate}
              value={copy.gateStatus[matrix?.productionGate.status ?? "blocked"]}
              warning={matrix?.productionGate.status !== "ready"}
            />
            <Fact
              copy={copy}
              label={copy.proof.proofArtifacts}
              value={copy.proof.proofArtifactCount(
                (release.proofArtifactRefs ?? []).length,
              )}
              warning={(release.proofArtifactRefs ?? []).length === 0}
            />
          </dl>
        </article>

        <article className="min-w-0 bg-card p-4">
          <h4 className="text-xs font-semibold text-foreground">
            {copy.proof.receiptHeading}
          </h4>
          <dl className="mt-3 grid gap-2 text-xs">
            <Fact
              copy={copy}
              label={copy.proof.environment}
              value={deployment?.environment ?? copy.absent.notSelected}
            />
            <Fact
              copy={copy}
              label={copy.proof.operation}
              value={deployment?.operation ?? copy.absent.notStarted}
            />
            <Fact
              copy={copy}
              label={copy.proof.adapter}
              value={
                deployment?.provider ??
                release.targetProfile.deploymentProfile ??
                copy.absent.notRegistered
              }
              warning={
                !deployment?.provider &&
                !release.targetProfile.deploymentProfile
              }
            />
            <Fact
              copy={copy}
              label={copy.proof.status}
              value={deployment?.status ?? copy.absent.notStarted}
              warning={Boolean(deployment) && deployment?.status !== "verified"}
            />
            <Fact
              copy={copy}
              label={copy.proof.deploymentId}
              value={deployment?.deploymentId}
            />
            <Fact
              copy={copy}
              label={copy.proof.output}
              value={shortHash(deployment?.outputHash ?? release.outputHash)}
              warning={
                Boolean(deployment?.outputHash) &&
                deployment?.outputHash !== release.outputHash
              }
            />
            <RefFact
              copy={copy}
              label={copy.proof.receipt}
              value={deployment?.receiptRef}
            />
            <RefFact
              copy={copy}
              label={copy.proof.stagingGrant}
              value={release.executionGrant?.grantRef}
              status={release.executionGrant?.status}
              expiresAt={release.executionGrant?.expiresAt}
            />
            <RefFact
              copy={copy}
              label={copy.proof.authorization}
              value={release.authorization?.authorizationRef}
              status={release.authorization?.status}
              expiresAt={release.authorization?.expiresAt}
            />
          </dl>
          {deployment?.url && (
            <div className="mt-3 rounded-md border border-border bg-background p-2.5">
              <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                {verifiedUrl
                  ? copy.proof.verifiedUrlHeading
                  : copy.proof.pendingUrlHeading}
              </p>
              {verifiedUrl ? (
                <a
                  href={verifiedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 block break-all font-mono text-[11px] text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {verifiedUrl}
                </a>
              ) : (
                <p className="mt-1 text-[11px] leading-4 text-muted-foreground">
                  {copy.proof.withheldNote}
                </p>
              )}
            </div>
          )}
          <FailureReasons copy={copy} reasons={deploymentFailures} />
        </article>

        <article className="min-w-0 bg-card p-4">
          <h4 className="text-xs font-semibold text-foreground">
            {copy.proof.liveCheckHeading}
          </h4>
          <dl className="mt-3 grid gap-2 text-xs">
            <Fact
              copy={copy}
              label={copy.proof.status}
              value={readback?.status ?? copy.absent.notStarted}
              warning={Boolean(readback) && readback?.status !== "pass"}
            />
            <Fact
              copy={copy}
              label={copy.proof.artifactMatch}
              value={proofBooleanLabel(
                readback?.deploymentArtifactMatch,
                copy,
              )}
              warning={readback?.deploymentArtifactMatch === false}
            />
            <Fact
              copy={copy}
              label={copy.proof.liveServer}
              value={proofBooleanLabel(readback?.liveServerMatch, copy)}
              warning={readback?.liveServerMatch === false}
            />
            <Fact
              copy={copy}
              label={copy.proof.checked}
              value={formatPublicationDate(readback?.checkedAt)}
            />
            <RefFact
              copy={copy}
              label={copy.proof.readback}
              value={readback?.readbackRef}
            />
          </dl>
          <FailureReasons copy={copy} reasons={readbackFailures} />
          {!readback && (
            <p className="mt-3 text-[11px] leading-4 text-muted-foreground">
              {copy.proof.noProofNote}
            </p>
          )}
        </article>
      </div>
    </details>
  );
}

function RefFact({
  copy,
  label,
  value,
  status,
  expiresAt,
}: {
  copy: ReleaseCopy;
  label: string;
  value?: { ref: string; contentHash: string };
  status?: string;
  expiresAt?: string;
}) {
  return (
    <div className="grid grid-cols-[100px_minmax(0,1fr)] gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-right">
        {value ? (
          <>
            <code className="block truncate text-foreground" title={value.ref}>
              {value.ref}
            </code>
            <code
              className="mt-0.5 block truncate text-[10px] text-muted-foreground"
              title={value.contentHash}
            >
              {value.contentHash}
            </code>
            {(status || expiresAt) && (
              <span className="mt-0.5 block text-[10px] text-muted-foreground">
                {[status, formatPublicationDate(expiresAt)]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            )}
          </>
        ) : (
          <span className="text-muted-foreground">{copy.notAvailable}</span>
        )}
      </dd>
    </div>
  );
}

function FailureReasons({
  copy,
  reasons,
}: {
  copy: ReleaseCopy;
  reasons: string[];
}) {
  if (reasons.length === 0) return null;
  return (
    <div className="mt-3 rounded-md border border-destructive/20 bg-destructive/10 p-2.5">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-destructive">
        {copy.proof.failureReasonsHeading}
      </p>
      <ul className="mt-1.5 space-y-1">
        {reasons.map((reason) => (
          <li key={reason} className="text-[11px] leading-4 text-destructive">
            {reason}
          </li>
        ))}
      </ul>
    </div>
  );
}

function AxisRow({
  copy,
  axis,
  proofArtifacts,
}: {
  copy: ReleaseCopy;
  axis: PublicationVerificationAxisProjection;
  proofArtifacts: Map<string, { contentHash: string }>;
}) {
  // §F4 — fourteen axes each reading a bare `pass` sat in the same card as
  // `Proof artifacts / 0 immutable ref(s)`. An axis whose evidence refs are
  // backed by no immutable artifact is REPORTED passing; it must not read
  // identically to one that is proven. Derived from the artifacts the
  // release actually carries, so it cannot drift from the count above it.
  const provenByImmutableArtifact =
    axis.evidenceRefs.length > 0 &&
    axis.evidenceRefs.some((ref) => proofArtifacts.has(ref));
  const reportedNotProven = axis.status === "pass" && !provenByImmutableArtifact;
  const Icon =
    reportedNotProven
      ? CircleDashed
      : axis.status === "pass"
        ? CheckCircle2
        : axis.status === "fail"
          ? XCircle
          : CircleDashed;
  return (
    <details
      className="group rounded-md border border-border bg-background px-3 py-2"
      data-testid={`publication-axis-${axis.axis}`}
    >
      <summary className="flex min-h-7 cursor-pointer list-none items-center gap-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <Icon
          className={`h-4 w-4 shrink-0 ${axis.status === "fail" ? "text-destructive" : "text-muted-foreground"}`}
          aria-hidden
        />
        <span className="min-w-0 flex-1 truncate font-medium text-foreground">
          {humanizeAxis(axis.axis)}
        </span>
        <span
          className={`text-[10px] ${axis.status === "fail" ? "text-destructive" : "text-muted-foreground"}`}
        >
          {reportedNotProven
            ? copy.matrix.axisReportedNotProven
            : copy.matrix.axisStatus[axis.status]}
        </span>
      </summary>
      <div className="mt-2 border-t border-border pt-2 text-[11px] leading-4 text-muted-foreground">
        {axis.evidenceRefs.length > 0 && (
          <ul className="space-y-1">
            {axis.evidenceRefs.map((ref) => {
              const proof = proofArtifacts.get(ref);
              return (
                <li key={ref} className="break-all font-mono">
                  <span>{ref}</span>
                  {proof && (
                    <span className="mt-0.5 block text-[10px] text-muted-foreground">
                      {proof.contentHash}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {axis.notes.length > 0 ? (
          <ul
            className={
              axis.evidenceRefs.length > 0 ? "mt-2 space-y-1" : "space-y-1"
            }
          >
            {axis.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        ) : axis.evidenceRefs.length === 0 ? (
          <p>{copy.matrix.axisNoEvidence}</p>
        ) : null}
      </div>
    </details>
  );
}

/**
 * §F8 — a deployment environment as a LABEL for a sentence, never the wire
 * value. `takeDownCurrent` remains the fallback for a release with no
 * deployment recorded.
 */
function environmentLabel(
  copy: ReleaseCopy,
  environment: string | undefined,
): string {
  if (environment === "production") return copy.production;
  if (environment === "staging") return copy.staging;
  return copy.confirm.takeDownCurrent;
}

function ReleaseStateBadge({
  release,
  locale,
}: {
  release: PublicationReleaseProjection | null;
  locale: LocaleCode;
}) {
  const state = release?.derivedState ?? "draft";
  const Icon =
    state === "production_ready"
      ? ShieldCheck
      : state === "failed" || state.includes("blocked")
        ? AlertTriangle
        : CircleDashed;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 font-mono text-[10px] ${
        state === "failed"
          ? "border-destructive/30 bg-destructive/10 text-destructive"
          : "border-border bg-muted text-foreground"
      }`}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {publicationStateLabel(state, locale)}
    </span>
  );
}

function PrimaryActionButton({
  label,
  busyLabel,
  busy,
  disabled,
  describedBy,
  title,
  icon: Icon,
  onClick,
}: {
  label: string;
  busyLabel: string;
  busy: boolean;
  disabled: boolean;
  /** Id of the on-screen reason this action is blocked, when it is. */
  describedBy?: string;
  title?: string;
  icon: typeof FileCheck2;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-describedby={describedBy}
      title={title}
      className="inline-flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-foreground bg-foreground px-4 text-xs font-semibold text-background transition-colors hover:bg-foreground/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40"
    >
      {busy ? (
        <Loader2
          className="h-4 w-4 animate-spin motion-reduce:animate-none"
          aria-hidden
        />
      ) : (
        <Icon className="h-4 w-4" aria-hidden />
      )}
      {busy ? busyLabel : label}
    </button>
  );
}

function EmptyReleaseState({
  title,
  detail,
}: {
  title: string;
  detail: string;
}) {
  return (
    <div className="rounded-md border border-dashed border-border bg-background px-4 py-6 text-center">
      <FileCheck2
        className="mx-auto h-5 w-5 text-muted-foreground"
        aria-hidden
      />
      <p className="mt-2 text-sm font-medium text-foreground">{title}</p>
      <p className="mx-auto mt-1 max-w-md text-xs leading-5 text-muted-foreground">
        {detail}
      </p>
    </div>
  );
}

function Fact({
  copy,
  label,
  value,
  warning = false,
}: {
  copy: ReleaseCopy;
  label: string;
  value?: string;
  warning?: boolean;
}) {
  return (
    <div className="grid grid-cols-[100px_minmax(0,1fr)] gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd
        className={`truncate text-right font-mono ${warning ? "text-destructive" : "text-foreground"}`}
        title={value ?? copy.notAvailable}
      >
        {value ?? copy.notAvailable}
      </dd>
    </div>
  );
}

function GateBadge({
  copy,
  label,
  status,
}: {
  copy: ReleaseCopy;
  label: string;
  status: "ready" | "blocked";
}) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-1 font-mono text-[10px] text-foreground">
      {status === "ready" ? (
        <CheckCircle2 className="h-3 w-3" aria-hidden />
      ) : (
        <CircleDashed className="h-3 w-3" aria-hidden />
      )}
      {label}: {copy.gateStatus[status]}
    </span>
  );
}

function ReleaseLedger({
  copy,
  locale,
  releases,
  current,
}: {
  copy: ReleaseCopy;
  locale: LocaleCode;
  releases: PublicationReleaseProjection[];
  current: PublicationReleaseProjection;
}) {
  if (releases.length === 0) return null;
  const historicalReleases = releases.filter(
    (item) => item.releaseId !== current.releaseId,
  );
  return (
    <details
      className="border-t border-border bg-card"
      data-testid="publication-release-ledger"
    >
      <summary className="flex min-h-12 cursor-pointer items-center justify-between gap-3 px-4 py-3 text-xs font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <span>{copy.ledger.summary}</span>
        <span className="font-mono text-[10px] font-normal text-muted-foreground">
          {copy.ledger.count(releases.length)}
        </span>
      </summary>
      <div className="max-h-72 overflow-auto border-t border-border">
        {historicalReleases.length > 0 ? (
          <ol className="divide-y divide-border">
            {historicalReleases.map((item) => (
              <li
                key={item.releaseId}
                className="grid gap-2 px-4 py-3 text-xs sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <code
                      className="truncate font-mono text-foreground"
                      title={item.releaseId}
                    >
                      {item.releaseId}
                    </code>
                  </div>
                  <p className="mt-1 truncate text-[11px] text-muted-foreground">
                    {item.targetProfile.profileId}@
                    {item.targetProfile.profileVersion} ·{" "}
                    {copy.ledger.manifestPrefix} {shortHash(item.outputHash)}
                  </p>
                </div>
                <div className="text-left sm:text-right">
                  <p className="font-mono text-[10px] text-foreground">
                    {publicationStateLabel(item.derivedState, locale)}
                  </p>
                  <time
                    dateTime={item.updatedAt}
                    className="mt-1 block text-[10px] text-muted-foreground"
                  >
                    {formatPublicationDate(item.updatedAt)}
                  </time>
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <p className="px-4 py-3 text-[11px] text-muted-foreground">
            {copy.ledger.noPrior}
          </p>
        )}
      </div>
      <p className="border-t border-border px-4 py-2 text-[10px] leading-4 text-muted-foreground">
        {copy.ledger.serverOwnedNote}
      </p>
    </details>
  );
}

function ExternalEffectConfirmation({
  copy,
  confirmation,
  busy,
  onOpenChange,
  onConfirm,
}: {
  copy: ReleaseCopy;
  confirmation: ConfirmationState | null;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog.Root open={Boolean(confirmation)} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[70] bg-black/50" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-[71] w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-background p-5 shadow-xl focus:outline-none"
          onEscapeKeyDown={(event) => {
            if (busy) {
              event.preventDefault();
              return;
            }
            onOpenChange(false);
          }}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="text-base font-semibold text-foreground">
                {confirmation?.title}
              </Dialog.Title>
              <Dialog.Description className="mt-2 text-sm leading-6 text-muted-foreground">
                {confirmation?.description}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                disabled={busy}
                aria-label={copy.confirm.closeAria}
                className="inline-flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </Dialog.Close>
          </div>
          <div
            role="alert"
            className="mt-4 flex gap-2 rounded-md border border-border bg-muted p-3 text-xs leading-5 text-foreground"
          >
            <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            {copy.confirm.serverRecheckNote}
          </div>
          <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Dialog.Close asChild>
              <button
                type="button"
                disabled={busy}
                className="inline-flex h-11 cursor-pointer items-center justify-center rounded-md border border-border px-4 text-xs font-medium text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
              >
                {copy.confirm.cancel}
              </button>
            </Dialog.Close>
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              className="inline-flex h-11 cursor-pointer items-center justify-center gap-2 rounded-md border border-foreground bg-foreground px-4 text-xs font-semibold text-background hover:bg-foreground/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
            >
              {busy ? (
                <Loader2
                  className="h-4 w-4 animate-spin motion-reduce:animate-none"
                  aria-hidden
                />
              ) : (
                <Rocket className="h-4 w-4" aria-hidden />
              )}
              {confirmation?.kind === "rollback"
                ? copy.confirm.confirmRollback
                : confirmation?.kind === "take-down"
                  ? copy.confirm.confirmTakeDown
                  : copy.confirm.confirmDeployment}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function selectPrimaryAction(
  copy: ReleaseCopy,
  mode: PublicationPanelMode,
  environment: ReleaseEnvironment,
  release: PublicationReleaseProjection | null,
  hasExecutionCredential: boolean,
  targetChanged: boolean,
): {
  action: PublicationReleaseAction;
  label: string;
  busyLabel: string;
  icon: typeof FileCheck2;
  /**
   * Why this offered action cannot be taken, in the session's language. Set
   * HERE, where the action is offered, so the user reads it before the
   * commit point instead of after a confirmation dialog. §5.3g.
   */
  blockedReason?: string;
} | null {
  if (!release) {
    return mode === "candidate"
      ? {
          action: "prepare-candidate",
          label: copy.actions.prepareCandidate,
          busyLabel: copy.actions.prepareCandidateBusy,
          icon: FileCheck2,
        }
      : null;
  }
  if (mode === "candidate") {
    if (targetChanged)
      return {
        action: "prepare-candidate",
        label: copy.actions.prepareForSelectedTarget,
        busyLabel: copy.actions.prepareCandidateBusy,
        icon: FileCheck2,
      };
    if (release.sourceRevision.stale)
      return {
        action: "prepare-candidate",
        label: copy.actions.prepareUpdated,
        busyLabel: copy.actions.prepareCandidateBusy,
        icon: RefreshCw,
      };
    if (release.allowedActions.includes("run-verification")) {
      return {
        action: "run-verification",
        label: copy.actions.runMissingChecks,
        busyLabel: copy.actions.runMissingChecksBusy,
        icon: ShieldCheck,
      };
    }
    return null;
  }

  const sameEnvironmentDeployment =
    release.deployment?.environment === environment;
  if (
    sameEnvironmentDeployment &&
    release.deployment &&
    !["failed", "rolled-back", "taken-down"].includes(release.deployment.status)
  ) {
    return publicationVerifiedDeploymentUrl(release)
      ? null
      : {
          action: "refresh-readback",
          label: copy.actions.refreshIndependentCheck,
          busyLabel: copy.actions.refreshIndependentCheckBusy,
          icon: RefreshCw,
        };
  }

  if (environment === "staging") {
    if (!hasExecutionCredential) {
      return {
        action: "request-staging-grant",
        label: copy.actions.requestStagingGrant,
        busyLabel: copy.actions.requestStagingGrantBusy,
        icon: LockKeyhole,
      };
    }
    if (!release.deployment || release.deployment.environment !== "staging") {
      return {
        action: "deploy-staging",
        label: copy.actions.deployStaging,
        busyLabel: copy.actions.deployStagingBusy,
        icon: Rocket,
      };
    }
  } else {
    if (!hasExecutionCredential) {
      return {
        action: "request-authorization",
        label: copy.actions.requestAuthorization,
        busyLabel: copy.actions.requestAuthorizationBusy,
        icon: LockKeyhole,
      };
    }
    if (
      !release.deployment ||
      release.deployment.environment !== "production"
    ) {
      // `promotionRef` is a fact of the release projection that the identity
      // card already renders as a warning row — so it is knowable at the
      // moment the button is offered. It used to be discovered by a `throw`
      // inside `confirmExternalEffect`, i.e. one press past the point of no
      // return.
      return {
        action: "deploy-production",
        label: copy.actions.deployProduction,
        busyLabel: copy.actions.deployProductionBusy,
        icon: Rocket,
        ...(release.promotionRef
          ? {}
          : { blockedReason: copy.guards.productionNeedsPromotion }),
      };
    }
  }

  return null;
}

/**
 * Returns `undefined` (not an English placeholder) for a missing hash: `Fact`
 * and `RefFact` own the localized "not available" fallback, so the absence
 * text is resolved once, through the copy table, instead of being minted here
 * where no locale is in scope.
 */
function shortHash(value: string | undefined): string | undefined {
  if (!value) return undefined;
  return value.length > 24 ? `${value.slice(0, 18)}…${value.slice(-6)}` : value;
}

function proofBooleanLabel(
  value: boolean | undefined,
  copy: ReleaseCopy,
): string {
  if (value === true) return copy.proofBoolean.match;
  if (value === false) return copy.proofBoolean.mismatch;
  return copy.proofBoolean.notChecked;
}

/** Same contract as `shortHash` — absence resolves at the `Fact` boundary. */
function formatPublicationDate(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

function humanizeAxis(axis: string): string {
  return axis
    .split("-")
    .map((part) => part.slice(0, 1).toUpperCase() + part.slice(1))
    .join(" ");
}
